'use client';

import { useEffect, useRef, useState } from 'react';
import Button from '@/components/ui/Button';
import Flag from '@/components/ui/Flag';
import type { Locale } from '@/i18n/locales';

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.611-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.82 11.82 0 00-3.48-8.413z" />
    </svg>
  );
}

type Slide = {
  code: Locale;
  label: string;
  flag: string;
  eyebrow: string;
  h1: [string, string];
  sub: string;
};

// English is index 0 so it renders in SSR (fail-open, readable with no JS); the
// rest rotate automatically until the visitor picks a language flag. No long
// dashes, per the site-wide rule.
const SLIDES: Slide[] = [
  {
    code: 'en',
    label: 'English',
    flag: '🇬🇧',
    eyebrow: 'Property Management & Caretaking',
    h1: ['Your Lapland property.', 'Cared for all year.'],
    sub: 'On-site caretaking, maintenance, permits and rental management at Ylläs, for owners in Finland and abroad.',
  },
  {
    code: 'fi',
    label: 'Suomi',
    flag: '🇫🇮',
    eyebrow: 'Kiinteistönhoito ja huolenpito',
    h1: ['Lappilainen kiinteistösi.', 'Hoidettuna ympäri vuoden.'],
    sub: 'Paikallista kiinteistönhoitoa, huoltoa, lupa-asioita ja vuokrauksen hallintaa Ylläksellä, omistajille Suomessa ja ulkomailla.',
  },
  {
    code: 'sv',
    label: 'Svenska',
    flag: '🇸🇪',
    eyebrow: 'Fastighetsskötsel och omsorg',
    h1: ['Din fastighet i Lappland.', 'Omskött året om.'],
    sub: 'Fastighetsskötsel, underhåll, tillstånd och uthyrning på plats i Ylläs, för ägare i Finland och utomlands.',
  },
  {
    code: 'de',
    label: 'Deutsch',
    flag: '🇩🇪',
    eyebrow: 'Immobilienverwaltung & Hausbetreuung',
    h1: ['Ihre Immobilie in Lappland.', 'Das ganze Jahr betreut.'],
    sub: 'Betreuung, Wartung, Genehmigungen und Vermietung vor Ort in Ylläs, für Eigentümer in Finnland und im Ausland.',
  },
  {
    code: 'fr',
    label: 'Français',
    flag: '🇫🇷',
    eyebrow: 'Gestion et entretien de biens',
    h1: ['Votre propriété en Laponie.', "Entretenue toute l'année."],
    sub: "Gardiennage, entretien, permis et gestion locative sur place à Ylläs, pour les propriétaires en Finlande et à l'étranger.",
  },
  {
    code: 'es',
    label: 'Español',
    flag: '🇪🇸',
    eyebrow: 'Gestión y cuidado de propiedades',
    h1: ['Su propiedad en Laponia.', 'Cuidada todo el año.'],
    sub: 'Cuidado, mantenimiento, permisos y gestión de alquileres in situ en Ylläs, para propietarios en Finlandia y en el extranjero.',
  },
  {
    code: 'nl',
    label: 'Nederlands',
    flag: '🇳🇱',
    eyebrow: 'Vastgoedbeheer & onderhoud',
    h1: ['Uw woning in Lapland.', 'Het hele jaar verzorgd.'],
    sub: 'Beheer, onderhoud, vergunningen en verhuurbeheer ter plaatse in Ylläs, voor eigenaren in Finland en daarbuiten.',
  },
];

export default function HeroRotator({
  whatsappHref,
  enEyebrow,
  enH1a,
  enH1b,
  enSub,
  ctaLabel,
  flagAriaPrefix,
}: {
  whatsappHref: string;
  /** Overrides for the English slide (editable via the Pages & Copy admin). */
  enEyebrow?: string;
  enH1a?: string;
  enH1b?: string;
  enSub?: string;
  ctaLabel?: string;
  /** Prefix for the language-flag aria-labels (e.g. "Show hero in"). */
  flagAriaPrefix?: string;
}) {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const [manual, setManual] = useState(false);
  const swapRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const manualSwapRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (manual) return; // visitor picked a language -> stop auto-rotating
    if (typeof window === 'undefined') return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    const interval = setInterval(() => {
      setVisible(false);
      swapRef.current = setTimeout(() => {
        setIndex((prev) => (prev + 1) % SLIDES.length);
        setVisible(true);
      }, 450);
    }, 4600);

    return () => {
      clearInterval(interval);
      if (swapRef.current) clearTimeout(swapRef.current);
    };
  }, [manual]);

  // Clear the manual-swap timer only on unmount, so the [manual] effect's cleanup
  // (which fires the instant a flag is picked) can't cancel the pending switch.
  useEffect(
    () => () => {
      if (manualSwapRef.current) clearTimeout(manualSwapRef.current);
    },
    []
  );

  const select = (i: number) => {
    setManual(true);
    if (i === index) return;
    setVisible(false);
    if (manualSwapRef.current) clearTimeout(manualSwapRef.current);
    manualSwapRef.current = setTimeout(() => {
      setIndex(i);
      setVisible(true);
    }, 200);
  };

  const slides = SLIDES.map((sl, i) =>
    i === 0
      ? {
          ...sl,
          eyebrow: enEyebrow ?? sl.eyebrow,
          h1: [enH1a ?? sl.h1[0], enH1b ?? sl.h1[1]] as [string, string],
          sub: enSub ?? sl.sub,
        }
      : sl
  );

  const s = slides[index];

  return (
    <>
      {/* Language flags, pinned to the hero top-right */}
      <div className="relative z-20 mb-7 flex flex-wrap justify-center gap-1 px-4">
        {slides.map((sl, i) => {
          const active = i === index;
          return (
            <button
              key={sl.code}
              type="button"
              onClick={() => select(i)}
              aria-label={`${flagAriaPrefix ?? 'Show hero in'} ${sl.label}`}
              aria-pressed={active}
              title={sl.label}
              className={
                'flex items-center justify-center w-11 h-11 rounded-md backdrop-blur-sm transition-all duration-200 ' +
                (active
                  ? 'ring-2 ring-[#C4A95E] ring-offset-2 ring-offset-black/30 bg-black/20'
                  : 'opacity-60 hover:opacity-100 bg-black/25')
              }
            >
              <Flag code={sl.code} className="w-5" />
            </button>
          );
        })}
      </div>

      {/* Hero content */}
      <div className="relative z-10 w-full max-w-4xl mx-auto px-4 sm:px-6 text-center">
        <div
          className="min-h-[15rem] sm:min-h-[16rem] flex flex-col justify-center"
          style={{ opacity: visible ? 1 : 0, transition: 'opacity 0.45s ease-out' }}
          lang={s.code}
        >
          <p className="font-sans text-xs font-semibold uppercase tracking-[0.18em] sm:tracking-[0.35em] mb-4 text-white/80">
            {s.eyebrow}
          </p>
          <h1 className="font-sans text-3xl sm:text-4xl lg:text-6xl font-light leading-tight mb-5 text-white">
            {s.h1[0]}
            <br />
            {s.h1[1]}
          </h1>
          <p className="font-body text-base sm:text-lg text-white/80 max-w-2xl mx-auto leading-relaxed">
            {s.sub}
          </p>
        </div>

        <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button href="#contact" variant="primary" size="lg">
            {ctaLabel ?? 'Request a care plan'}
          </Button>
          <a
            href={whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2.5 px-8 py-3.5 text-base rounded-full bg-[#25D366] text-white shadow-md hover:bg-[#1EBE57] transition-colors duration-200"
            style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontWeight: 700 }}
          >
            <WhatsAppIcon className="w-5 h-5" />
            WhatsApp
          </a>
        </div>
      </div>
    </>
  );
}
